import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import {
  Users, UserPlus, TrendingUp, Network, Briefcase, CalendarDays, BarChart3, Layers,
  ArrowRight, Check,
} from "lucide-react";

const CONTACT = "mailto:kendrick@innovationtalentpartners.com?subject=Fractional%20VC%20Platform%20Services";

const RESPONSIBILITIES = [
  { icon: Users, title: "Founder Support", body: "Regular check-ins and office hours to identify challenges and priorities." },
  { icon: UserPlus, title: "Talent", body: "Help founders develop hiring strategies, recruit executives, and access talent." },
  { icon: TrendingUp, title: "Fundraising", body: "Prepare companies for future rounds, review decks, identify investors, and facilitate introductions." },
  { icon: Network, title: "Customers & Partnerships", body: "Help founders identify potential customers, corporate partners, pilots, and ecosystem connections." },
  { icon: Briefcase, title: "Expert Resources", body: "Connect companies with attorneys, CFOs, technical experts, marketing firms, consultants, and other trusted providers." },
  { icon: CalendarDays, title: "Portfolio Programming", body: "Organize workshops, founder roundtables, investor sessions, and educational programming." },
  { icon: BarChart3, title: "Portfolio Intelligence", body: "Track the needs of companies and give VC partners regular reports on where companies need assistance." },
  { icon: Layers, title: "Platform Strategy", body: "Help the fund build a stronger founder experience and determine what resources it should offer portfolio companies." },
];

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function FractionalVCPlatform() {
  return (
    <div className="min-h-screen bg-white text-[#09090B]" data-testid="vc-platform-page">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28">
          <p className="overline mb-4">Fractional VC Platform Services</p>
          <motion.h1 {...fade} className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
            World-class platform support,<br /><span className="text-primary">without a full-time hire.</span>
          </motion.h1>
          <motion.p {...fade} transition={{ delay: 0.1 }} className="mt-8 text-lg text-muted-foreground max-w-2xl">
            Many funds want to offer their founders exceptional platform and portfolio support — but aren't yet ready
            to build a full-time platform team. We embed an experienced platform leader into your fund on a fractional
            basis, giving your portfolio the resources they need and your partners the leverage they want.
          </motion.p>
          <motion.div {...fade} transition={{ delay: 0.2 }} className="mt-10">
            <a href={CONTACT} data-testid="vc-cta-top" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary text-white hover:bg-[#09090B] transition-colors">
              Book a Strategy Call <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <p className="overline mb-3">What we do</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl max-w-2xl">Typical responsibilities of a fractional platform lead.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {RESPONSIBILITIES.map((r, i) => (
              <motion.div {...fade} transition={{ delay: (i % 4) * 0.05 }} key={r.title} data-testid={`vc-responsibility-${i}`} className="bg-white p-7 flex flex-col">
                <div className="h-11 w-11 bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <r.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading font-bold text-lg leading-tight">{r.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-3">Who it's for</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight">Built for funds that punch above their size.</h2>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-4">
              {[
                "Emerging managers who want a differentiated founder experience from day one.",
                "Established funds testing platform before committing to a full-time hire.",
                "Funds between platform leaders who need continuity and coverage.",
                "Corporate and strategic investors building out portfolio support.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 border border-black/10 p-5">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-1" strokeWidth={2.5} />
                  <span className="text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center relative">
          <h2 className="font-heading font-black text-3xl sm:text-4xl">Give your founders the platform they deserve.</h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            Let's talk about how a fractional platform lead can strengthen your portfolio and free up your partners.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href={CONTACT} data-testid="vc-cta-bottom" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-white text-primary hover:bg-[#09090B] hover:text-white transition-colors">
              Book a Strategy Call <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/advisory-services" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-white/25 hover:bg-white/10 transition-colors">
              Explore advisory services
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
